/**
 * Keys made on the account's Keys & Sessions page (tokens), and `hookdeck login` keys, can have an
 * expiry and a limit: one organization, and optionally only some of its projects. They're picked
 * when the token is made or the login approved. Keys without them pass every check here.
 */

// The expiry choices, in days, offered when creating a token or approving a login. No expiry is
// the default.
export const CLI_TOKEN_EXPIRY_DAYS = [7, 30, 90, 365] as const;

export type CliTokenExpiryDays = (typeof CLI_TOKEN_EXPIRY_DAYS)[number];

export interface CliTokenLimits {
  expires_at?: Date | string | null;
  organization_id?: string | null;
  team_ids?: string[] | null;
}

export const isCliTokenExpired = (
  cli_client: Pick<CliTokenLimits, 'expires_at'>,
  now: number = Date.now()
): boolean => !!cli_client.expires_at && new Date(cli_client.expires_at).getTime() <= now;

export const canCliClientReachOrganization = (
  cli_client: CliTokenLimits,
  organization_id: string
): boolean => !cli_client.organization_id || cli_client.organization_id === organization_id;

// Checks the project limit even without an organization limit, so a malformed row stays limited.
export const canCliClientReachTeam = (
  cli_client: CliTokenLimits,
  team: { id: string; organization_id: string }
): boolean =>
  canCliClientReachOrganization(cli_client, team.organization_id) &&
  (!cli_client.team_ids || cli_client.team_ids.includes(team.id));

/**
 * Organization-wide actions (creating a project, changing the organization, managing its API keys)
 * need a key that reaches every project of the organization.
 */
export const canCliClientManageOrganization = (
  cli_client: CliTokenLimits,
  organization_id: string
): boolean => !cli_client.team_ids && canCliClientReachOrganization(cli_client, organization_id);

// A key limited to one organization, or to some of its projects.
export const isCliClientLimited = (cli_client: CliTokenLimits): boolean =>
  !!cli_client.organization_id || !!cli_client.team_ids;

/**
 * Only a key with full access and no expiry can create keys. Otherwise a key could make itself a
 * new one with more access or a longer life.
 */
export const canCliClientCreateKeys = (cli_client: CliTokenLimits): boolean =>
  !isCliClientLimited(cli_client) && !cli_client.expires_at;
